import {
  type AgentProfile,
  useInvalidateUseGetAgentProfile,
  useInvalidateUseGetAgentsInAgency,
  useUpdateAgentProfile,
} from '@urbanx/agx.agencies.client';
import { AgxFormTextField } from '@urbanx/agx.forms';
import {
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTextInput,
  SAVE_BUTTON_STATES,
  useToast,
} from '@urbanx/agx.ui-components2';
import { useMemo } from 'react';

import ProfilePhotoUpload from './ProfilePhotoUpload';
import { ProfileForm } from './profileSettingsFormDefinition';
import '../ProfileSettingsPage.scss';

interface ProfileSettingsFormProps {
  agentProfile: AgentProfile;
}

const ProfileSettingsForm = ({ agentProfile }: ProfileSettingsFormProps) => {
  const { pushToast } = useToast();
  const defaultValues = useMemo(
    () => ({
      firstName: agentProfile.firstName,
      lastName: agentProfile.lastName,
      title: agentProfile.title ?? '',
      phone: agentProfile.phone ?? '',
      photo: agentProfile.photo ?? undefined,
    }),
    [
      agentProfile.firstName,
      agentProfile.lastName,
      agentProfile.phone,
      agentProfile.photo,
      agentProfile.title,
    ]
  );
  const invalidateAgentProfile = useInvalidateUseGetAgentProfile();
  const invalidateAgentsInAgency = useInvalidateUseGetAgentsInAgency();
  const { updateAgentProfile, updateAgentProfilePending } = useUpdateAgentProfile({
    onSuccess: () => {},
    onError: () => {
      pushToast({
        type: 'error',
        message: 'Unable to save profile changes',
      });
    },
  });
  const form = ProfileForm.useForm({
    defaultValues,
    onSubmit: async ({ parsedValues }) => {
      try {
        await updateAgentProfile(parsedValues);
        try {
          await Promise.all([invalidateAgentProfile(), invalidateAgentsInAgency()]);
        } catch {
          pushToast({
            type: 'error',
            message: 'Profile saved, but refreshed details could not be loaded',
          });
        }
        return { status: 'accepted', baseline: parsedValues };
      } catch (error) {
        return { status: 'failed', error };
      }
    },
  });

  return (
    <ProfileForm.Root form={form} className="profile-settings">
      <AgxSpacer gap="spacing-3xl">
        <ProfileForm.CustomField name="photo">
          {(field) => (
            <ProfilePhotoUpload
              disabled={updateAgentProfilePending}
              firstName={form.values.firstName}
              lastName={form.values.lastName}
              photo={field.value}
              onPhotoChanged={field.onChange}
            />
          )}
        </ProfileForm.CustomField>

        <AgxSpacer gap="spacing-2xl">
          <AgxTextInput
            id="profile-email"
            type="email"
            label="Email"
            hideOptionalText
            stretch
            defaultValue={agentProfile.email}
            disabled
          />
          <AgxSpacer direction="horizontal" gap="spacing-2xl" wrap>
            <ProfileForm.Field
              name="firstName"
              Input={AgxFormTextField}
              type="text"
              label="First name"
              hideOptionalText
              stretch
            />
            <ProfileForm.Field
              name="lastName"
              Input={AgxFormTextField}
              type="text"
              label="Last name"
              hideOptionalText
              stretch
            />
          </AgxSpacer>

          <ProfileForm.Field
            name="title"
            Input={AgxFormTextField}
            type="text"
            label="Job title"
            hideOptionalText
            stretch
          />
          <ProfileForm.Field
            name="phone"
            Input={AgxFormTextField}
            type="phone"
            label="Phone"
            hideOptionalText
            stretch
          />
        </AgxSpacer>

        <AgxSpacer direction="horizontal" contentJustification="start">
          <ProfileForm.Submit>
            {({ submit, phase, isDirty }) => {
              const saveButtonState = updateAgentProfilePending
                ? SAVE_BUTTON_STATES.SAVING
                : phase === 'succeeded' && !isDirty
                  ? SAVE_BUTTON_STATES.SAVED
                  : SAVE_BUTTON_STATES.UNSAVED;

              return (
                <AgxSaveChangesButton
                  id="save-agent-profile"
                  text="Save changes"
                  variant="primary"
                  size="medium"
                  state={saveButtonState}
                  onClick={() => void submit()}
                  disabled={!isDirty || updateAgentProfilePending}
                />
              );
            }}
          </ProfileForm.Submit>
        </AgxSpacer>
      </AgxSpacer>
    </ProfileForm.Root>
  );
};

export default ProfileSettingsForm;
